// frontend/src/modules/projects/pages/ReportsPage.tsx
import * as React from 'react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { DataTable } from '../../../design-system/components/DataTable';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { useApi } from '../../../design-system/useApi';
import { formatDate } from '../../../design-system/format';
import { VISUAL } from '../../../design-system/visual';
import { motion, LayoutGroup } from 'framer-motion';
import type {
  ReportRowByProject, ReportRowByEmployee, ReportRowByTask, DailyTimelineRow,
} from '../types';

type Preset = 'today' | 'this_week' | 'this_month' | 'last_30';
function rangeFor(p: Preset): { from: string; to: string } {
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  function iso(d: Date) { return d.toISOString().slice(0, 10); }
  if (p === 'today') return { from: today, to: today };
  if (p === 'this_week') {
    const day = now.getUTCDay();             // 0=Sun, 1=Mon...
    const monOffset = (day + 6) % 7;          // days since Monday
    const monday = new Date(now); monday.setUTCDate(now.getUTCDate() - monOffset);
    return { from: iso(monday), to: today };
  }
  if (p === 'this_month') {
    const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    return { from: iso(first), to: today };
  }
  const back = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 29));
  return { from: iso(back), to: today };
}

function hrs(m: number): string { return `${Math.floor(m / 60)}h ${m % 60}m`; }

export function ReportsPage() {
  const [preset, setPreset] = React.useState<Preset>('this_week');
  const { from, to } = rangeFor(preset);

  const byProject  = useApi<{ rows: ReportRowByProject[]  }>(`/api/projects/reports/by-project?from=${from}&to=${to}`, [preset]);
  const byEmployee = useApi<{ rows: ReportRowByEmployee[] }>(`/api/projects/reports/by-employee?from=${from}&to=${to}`, [preset]);
  const byTask     = useApi<{ rows: ReportRowByTask[]     }>(`/api/projects/reports/by-task?from=${from}&to=${to}`, [preset]);
  const timeline   = useApi<{ rows: DailyTimelineRow[]    }>(`/api/projects/reports/timeline?from=${from}&to=${to}`, [preset]);

  return (
    <div>
      <PageHeader
        eyebrow="Work / Projects / Reports"
        title="Reports"
        description={`Time aggregates from ${formatDate(from)} to ${formatDate(to)}.`}
        actions={
          <LayoutGroup id="reportPresets">
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-muted/50 ring-1 ring-border/40">
              <PresetButton active={preset === 'today'}      onClick={() => setPreset('today')}>Today</PresetButton>
              <PresetButton active={preset === 'this_week'}  onClick={() => setPreset('this_week')}>This week</PresetButton>
              <PresetButton active={preset === 'this_month'} onClick={() => setPreset('this_month')}>This month</PresetButton>
              <PresetButton active={preset === 'last_30'}    onClick={() => setPreset('last_30')}>Last 30 days</PresetButton>
            </div>
          </LayoutGroup>
        }
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-6xl space-y-6">
        {/* Reports are manager-gated server-side — a 403 previously rendered
            four friendly "No data" tables. Be honest about access instead. */}
        {byProject.status === 403 ? (
          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <NoAccessState description="Project reports are available to workspace owners and project managers." />
          </div>
        ) : (
        <>
        <Section title="By project">
          <DataTable<ReportRowByProject>
            loading={byProject.loading} rows={byProject.data?.rows ?? []}
            rowKey={(r) => r.project_id}
            columns={[
              { key: 'name',  header: 'Project', cell: (r) => <span className="font-medium text-foreground">{r.project_name}</span> },
              { key: 'mins',  header: 'Time',    cell: (r) => <span className="font-mono tabular-nums">{hrs(r.total_minutes)}</span> },
              { key: 'count', header: 'Entries', cell: (r) => r.entry_count, className: 'text-muted-foreground' },
            ]}
            emptyState={
              byProject.error
                ? <ErrorState title="Couldn't load this report" description={byProject.error} onRetry={byProject.reload} />
                : <EmptyState title="No data" description="No time entries in this range." />
            }
          />
        </Section>
        <Section title="By employee">
          <DataTable<ReportRowByEmployee>
            loading={byEmployee.loading} rows={byEmployee.data?.rows ?? []}
            rowKey={(r) => r.employee_id}
            columns={[
              { key: 'name',  header: 'Employee', cell: (r) => <span className="font-medium text-foreground">{r.employee_name}</span> },
              { key: 'mins',  header: 'Time',     cell: (r) => <span className="font-mono tabular-nums">{hrs(r.total_minutes)}</span> },
              { key: 'count', header: 'Entries',  cell: (r) => r.entry_count, className: 'text-muted-foreground' },
            ]}
            emptyState={
              byEmployee.error
                ? <ErrorState title="Couldn't load this report" description={byEmployee.error} onRetry={byEmployee.reload} />
                : <EmptyState title="No data" description="No time entries in this range." />
            }
          />
        </Section>
        <Section title="By task">
          <DataTable<ReportRowByTask>
            loading={byTask.loading} rows={byTask.data?.rows ?? []}
            rowKey={(r) => r.task_id ?? '_no_task'}
            columns={[
              { key: 'name',  header: 'Task',    cell: (r) => <span className="font-medium text-foreground">{r.task_title}</span> },
              { key: 'mins',  header: 'Time',    cell: (r) => <span className="font-mono tabular-nums">{hrs(r.total_minutes)}</span> },
              { key: 'count', header: 'Entries', cell: (r) => r.entry_count, className: 'text-muted-foreground' },
            ]}
            emptyState={
              byTask.error
                ? <ErrorState title="Couldn't load this report" description={byTask.error} onRetry={byTask.reload} />
                : <EmptyState title="No data" description="No time entries in this range." />
            }
          />
        </Section>
        <Section title="Daily timeline">
          <DataTable<DailyTimelineRow>
            loading={timeline.loading} rows={timeline.data?.rows ?? []}
            rowKey={(r) => r.date}
            columns={[
              { key: 'date',  header: 'Date',    cell: (r) => <span className="font-medium text-foreground">{formatDate(r.date)}</span> },
              { key: 'mins',  header: 'Time',    cell: (r) => <span className="font-mono tabular-nums">{hrs(r.total_minutes)}</span> },
              { key: 'count', header: 'Entries', cell: (r) => r.entry_count, className: 'text-muted-foreground' },
            ]}
            emptyState={
              timeline.error
                ? <ErrorState title="Couldn't load this report" description={timeline.error} onRetry={timeline.reload} />
                : <EmptyState title="No data" description="No time entries in this range." />
            }
          />
        </Section>
        </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className={[VISUAL.dashSection, 'mb-3'].join(' ')}>{title}</h3>
      <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
        {children}
      </div>
    </section>
  );
}

function PresetButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'relative inline-flex items-center px-3.5 h-8 text-[12px] font-semibold tracking-[-0.005em] transition-colors',
        active ? 'text-zinc-950' : 'text-muted-foreground hover:text-foreground',
      ].join(' ')}
    >
      {active && (
        <motion.span
          layoutId="reportsPresetActive"
          className="absolute inset-0 -z-10 rounded-full bg-saffron"
          transition={{ type: 'spring', stiffness: 350, damping: 30 }}
        />
      )}
      <span className="relative">{children}</span>
    </button>
  );
}
